import { Button, ConfirmDialog, PageHeader, Skeleton } from '@estac1/ui';
import { Save } from 'lucide-react';
import {
  AssistantCard,
  BillingFlagsCard,
  ModulesCatalogCard,
  ObservabilityCard,
  RetentionCard,
} from './pricing-settings/billing-settings-cards.js';
import {
  PricingDriftCard,
  PricingHistoryCard,
  PricingLimitsCard,
  PricingPreviewCard,
  PricingTiersCard,
} from './pricing-settings/pricing-editor-cards.js';
import { usePricingSettings } from './pricing-settings/use-pricing-settings.js';

export { BillingFlagsCard } from './pricing-settings/billing-settings-cards.js';

export const PricingSettingsScreen = () => {
  const {
    settings,
    audit,
    drift,
    recalc,
    recalcConfirm,
    tiers,
    minimumCents,
    maxVacancies,
    trialDays,
    previewVacancies,
    expandedHistory,
    validationError,
    previewSamples,
    pricingHistory,
    save,
    setMinimumCents,
    setMaxVacancies,
    setTrialDays,
    setPreviewVacancies,
    toggleHistory,
    updateTier,
    addTier,
    removeTier,
    handleSave,
  } = usePricingSettings();

  if (settings.isLoading) {
    return (
      <div className="space-y-4">
        <PageHeader title="Preços" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Preços"
        description="Editáveis em runtime. Mudanças propagam em até 5 minutos para os clientes."
        actions={
          <Button onClick={handleSave} disabled={save.isPending || validationError !== null}>
            <Save className="h-4 w-4" /> Salvar
          </Button>
        }
      />

      {validationError && (
        <div
          className="rounded border border-warning/40 bg-warning/10 p-3 text-sm text-warning-fg"
          role="alert"
        >
          {validationError}
        </div>
      )}

      <BillingFlagsCard />
      <ModulesCatalogCard />
      <RetentionCard />
      <ObservabilityCard />
      <AssistantCard />

      <PricingLimitsCard
        minimumCents={minimumCents}
        maxVacancies={maxVacancies}
        trialDays={trialDays}
        onMinimumCents={setMinimumCents}
        onMaxVacancies={setMaxVacancies}
        onTrialDays={setTrialDays}
      />

      <PricingTiersCard
        tiers={tiers}
        onUpdateTier={updateTier}
        onAddTier={addTier}
        onRemoveTier={removeTier}
      />

      <PricingPreviewCard
        previewVacancies={previewVacancies}
        onPreviewVacancies={setPreviewVacancies}
        previewSamples={previewSamples}
        validationError={validationError}
      />

      <PricingDriftCard
        drift={drift}
        recalc={recalc}
        onRecalc={() => recalcConfirm.confirm(() => recalc.mutateAsync())}
      />

      <PricingHistoryCard
        auditLoading={audit.isLoading}
        pricingHistory={pricingHistory}
        expandedHistory={expandedHistory}
        onToggleHistory={toggleHistory}
      />

      <ConfirmDialog
        {...recalcConfirm.dialogProps}
        title="Recalcular todos os clientes?"
        description={`Aplica o preço atual em ${drift.data?.count ?? 0} clientes. Cada assinatura é atualizada no provedor com cobrança proporcional.`}
        confirmLabel="Recalcular"
        pending={recalc.isPending}
      />
    </div>
  );
};
